import { action, computed, observable } from 'mobx'
import type { ISchemaBoundValidationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { createEntityIdValidation } from '@/features/legends/editor/entity-ids'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import type { KnownRoutingContextSource } from './data-sources'

/**
 * Base for routing data sources: data about the candidate pro, exposed to
 * routing filters under an ID. Each type validates itself with its own
 * schema, using {@link RoutingContextSourceEditorViewModelBase.idValidation}
 * for the ID.
 */
export abstract class RoutingContextSourceEditorViewModelBase<
  TSource extends KnownRoutingContextSource = KnownRoutingContextSource,
> extends LegendPartViewModelBase {
  protected static readonly idValidation =
    createEntityIdValidation<RoutingContextSourceEditorViewModelBase>(
      (source) => source.dependencies.symbols.allEntities,
    )

  /**
   * Identifies the source on the client, for keys.
   */
  readonly cid = newCid('source')

  /**
   * The source type, as stored in the legend.
   */
  abstract readonly type: TSource['type']

  /**
   * The type of the value routing filters see.
   */
  abstract readonly valueType: RynoType

  /**
   * The source's validation, bound to its type's schema.
   */
  abstract readonly validation: ISchemaBoundValidationContext<RoutingContextSourceEditorViewModelBase>

  /**
   * The ID filters reference the source by.
   */
  @observable accessor id = ''

  /**
   * The ID as typed. It becomes the ID once committed, if it is valid.
   */
  @observable accessor idDraft = ''

  constructor(dependencies: LegendPartDependencies, defaultId: string) {
    super(dependencies)
    this.id = defaultId
    this.idDraft = defaultId
  }

  /**
   * The source, as an entity routing filters can reference.
   */
  @computed
  get entityDescriptors(): EntityDescriptor[] {
    return [{ type: this.type, entity: this }]
  }

  /**
   * Sets the ID to the draft, when it is valid and unique.
   */
  @action.bound
  commitId() {
    RoutingContextSourceEditorViewModelBase.idValidation.trySetId(
      this,
      this.idDraft,
    )
  }

  /**
   * Sets the source from the legend document.
   */
  @action
  hydrate(source: TSource) {
    if (source.id) {
      this.id = source.id
      this.idDraft = source.id
    }
  }

  /**
   * The source as the legend document has it.
   */
  abstract toJSON(): TSource

  /**
   * Shows the analyzer's errors for the source.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }
}
